// Branded primitives (P0.2).
//
// Everything addressing a SAB ring is a `number`, and the bugs come from mixing them up:
// handing a monotonic sequence counter to something expecting an index already masked into
// the ring, or bytes to something counting slots. Those mistakes type-check perfectly and
// then corrupt audio on a thread with a 2.7 ms deadline — the worst place in the product to
// be holding a debugger.
//
// The brand exists only in the type system. Nothing is emitted, so the real-time read/write
// paths in P0.3 stay allocation-free (Playbook: "real-time paths never allocate").

declare const BRAND: unique symbol;

/** Attaches a compile-time-only tag to a primitive so it stops being interchangeable. */
export type Brand<TValue, TBrand extends string> = TValue & { readonly [BRAND]: TBrand };

/** A byte offset from the start of a SharedArrayBuffer. */
export type ByteOffset = Brand<number, 'ByteOffset'>;

/** An element index into an Int32Array view — what Atomics operate on. Not a byte offset. */
export type Int32Index = Brand<number, 'Int32Index'>;

/**
 * A count of slots ever written or read, increasing forever. We never wrap it by hand: it
 * wraps naturally at 2^32 and `sequenceDistance` is built to survive that (see ring-layout).
 */
export type Sequence = Brand<number, 'Sequence'>;

/** A Sequence masked into the ring: always 0 .. capacitySlots-1. */
export type SlotIndex = Brand<number, 'SlotIndex'>;

/** A sample-frame index within a track or an audio block. */
export type FrameIndex = Brand<number, 'FrameIndex'>;

/**
 * A timestamp on the AudioContext clock, in seconds.
 *
 * Deliberately incompatible with performance.now() milliseconds. Architecture §7 gives the
 * audio thread ownership of transport time specifically so the UI never computes it from its
 * own clock — "this single rule eliminates an entire class of drift and desync bugs". The
 * brand is that rule, enforced by the compiler instead of by memory.
 */
export type AudioTime = Brand<number, 'AudioTime'>;

export const asByteOffset = (value: number): ByteOffset => value as ByteOffset;
export const asInt32Index = (value: number): Int32Index => value as Int32Index;
export const asSequence = (value: number): Sequence => value as Sequence;
export const asSlotIndex = (value: number): SlotIndex => value as SlotIndex;
export const asFrameIndex = (value: number): FrameIndex => value as FrameIndex;
export const asAudioTime = (value: number): AudioTime => value as AudioTime;
